/**
 * Client-side state for "my work".
 *
 * The model after the workspace overhaul:
 *  - CALLS are workspace tabs. Each call tab is the curated route /call/:itemId — the
 *    decision tree runs inside that window. `openCall` activates + navigates there; a call
 *    that starts ringing auto-opens its tab — but only when the person is free. The server
 *    decides: a ringing call arriving while they are ON a live call is added to `tabs`
 *    WITHOUT becoming `active`, so the effect below never navigates and the conversation in
 *    progress is not interrupted (see docs/work-items.md, "A ringing call never steals the
 *    screen"). The client also protects an open call workspace during wrap-up. Answering
 *    or claiming the incoming call makes the takeover wanted.
 *  - TASKS live only in the sidebar (note / complete / report issue). No tabs, no activation.
 *  - HOME is the free-roam CRM (all the normal routes); the tab strip's Home button returns
 *    to the last Home page (localStorage).
 *
 * `mine` is the latest snapshot (SSE /resources/work-stream + every action's response).
 * `act(intent)` posts to /resources/work and swaps the returned snapshot in.
 * All clicks/navigations are recorded to the activity trail, attributed to the active call
 * (or none) — see activity.client.ts.
 */
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import { useLocation, useNavigate } from 'react-router'
import {
	installActivityRecorder,
	recordActivity,
	setActiveItemForActivity,
} from './activity.client.ts'
import { useOptionalUser } from './user.ts'
import { type WorkItemView, type WorkMine } from './work-api.server.ts'

export type WorkAction =
	| { intent: 'caller_customer'; id: number; st_customer_id: number }
	| { intent: 'refresh' }
	| { intent: 'pickup'; call: number; pid: number }
	| { intent: 'activate'; id: number; route?: string }
	| { intent: 'deactivate' }
	| {
			intent: 'answer'
			id: number
			step: string
			value: string
			route?: string
			/** "Tech calling": the two answers, `{ why, help }` */
			details?: Record<string, unknown>
	  }
	| {
			intent: 'step_done'
			id: number
			step: string
			result?: Record<string, unknown>
			route?: string
	  }
	| { intent: 'finish'; id: number; outcome?: string; cancel?: boolean }
	| { intent: 'reopen'; id: number }
	| { intent: 'close_tab'; id: number }
	| { intent: 'claim'; id: number }
	| { intent: 'reclassify'; id: number }
	| { intent: 'note'; id: number; text: string }
	| { intent: 'block'; id: number; reason?: string }
	| { intent: 'unblock'; id: number }
	| {
			intent: 'customer'
			id: number
			name?: string
			st_customer_id?: number
			phone?: string
	  }
	| {
			intent: 'create'
			title: string
			flow_id?: string
			priority?: number
			assignee_email?: string
			due_at?: string
			context?: Record<string, unknown>
	  }

type ActResult =
	| { ok: true; mine: WorkMine | null; createdId?: number }
	| { ok: false; error: string }

export type WorkConnection = 'connected' | 'reconnecting' | 'auth_lost'

export type WorkContextValue = {
	/** null until the first snapshot arrives */
	mine: WorkMine | null
	connected: boolean
	/** the stream's state: reconnects run a capped exponential backoff;
	 * 'auth_lost' means the session expired — only a reload helps */
	connection: WorkConnection
	busy: boolean
	lastError: string | null
	act: (action: WorkAction) => Promise<ActResult>
	/** open a call's workspace tab (activate + go to /call/:id) */
	openCall: (item: WorkItemView) => void
	/** back to Home (deactivates the call tab, returns to the last Home page) */
	goHome: () => void
	/** current URL (path + search) */
	here: string
}

const WorkContext = createContext<WorkContextValue | null>(null)

const EMPTY: WorkMine = {
	email: '',
	active: null,
	open: [],
	history: [],
	tabs: [],
	as_of: '',
}

const HOME_KEY = 'hep.home.route'

/** ?callHistory=1 — the mark of a "history (new tab)" link. */
function hasHistoryFlag(search: string): boolean {
	return new URLSearchParams(search).get('callHistory') === '1'
}

/**
 * What a history tab reads: the history viewers AND the detail pages they link
 * to (invoice, estimate, a past call, call search). The call log (/calls) is a
 * working page, not a record.
 */
const RECORDS_PAGE =
	/^\/(customers|locations|jobs|search|invoices|estimates|calls\/\d+|calls\/search)(\/|$)/

/** The same route without the history flag (other query parameters kept). */
function withoutHistoryFlag(route: string): string {
	const at = route.indexOf('?')
	if (at < 0) return route
	const params = new URLSearchParams(route.slice(at + 1))
	params.delete('callHistory')
	const rest = params.toString()
	return rest ? `${route.slice(0, at)}?${rest}` : route.slice(0, at)
}

/**
 * The basic caller-ID section (routes/callerid): its own small shell, meant
 * to be bookmarked on a phone. It shows the ringing call itself, so it is
 * never yanked to the call workspace, and it is never a Home page to return to.
 */
const BASIC_SHELL = /^\/callerid(\/|$)/

/**
 * Live Calls (routes/live): someone reading a call there is never pulled off
 * it. A pop-up on those pages (components/live-call-popup.tsx) says a call is
 * ringing or on, and opens it on a click.
 */
export const LIVE_CALLS = /^\/live(\/|$)/

/** Home pages worth returning to (never auth/settings/resource routes, call tabs or the basic shell) */
function isHomePage(pathname: string): boolean {
	return (
		!/^\/(resources|login|logout|signup|onboarding|verify|settings|admin|call)(\/|$)/.test(
			pathname,
		) && !BASIC_SHELL.test(pathname)
	)
}

/**
 * The stream and every action response both deliver a full snapshot, over two
 * connections, so they can arrive out of order. An ACTION RESPONSE built before
 * the payload already held (`as_of`) is dropped: a delayed response must not
 * put back a customer/step/tab the server no longer holds. When either side has
 * no usable timestamp the comparison is false and the response is applied,
 * exactly as before.
 *
 * STREAM snapshots never go through this. `as_of` is a server wall clock, so a
 * payload stamped by a fast clock (a second web machine, a clock that later
 * steps back) would otherwise make every earlier-stamped snapshot — a ringing
 * or answered call included — vanish until the slow clock caught up. A wedged
 * workspace during a live call is far worse than the flicker this guard cures,
 * and the stream is ordered on its own connection anyway.
 */
function newest(held: WorkMine | null, incoming: WorkMine): WorkMine {
	return held && Date.parse(held.as_of) > Date.parse(incoming.as_of)
		? held
		: incoming
}

export function WorkProvider({ children }: { children: ReactNode }) {
	const user = useOptionalUser()
	const location = useLocation()
	const navigate = useNavigate()
	const [mine, setMine] = useState<WorkMine | null>(null)
	const [connection, setConnection] = useState<WorkConnection>('connected')
	const [busy, setBusy] = useState(false)
	const [lastError, setLastError] = useState<string | null>(null)
	const here = location.pathname + location.search

	// --- stream ---------------------------------------------------------------------------
	// The browser's own EventSource retry gives up permanently once the server
	// answers with anything but a stream (deploy blips, auth redirects), so we
	// own the lifecycle: close on error, reconnect with capped exponential
	// backoff (1s → 30s), and after repeated immediate failures probe a cheap
	// authenticated endpoint once — EventSource can't see the HTTP status, but
	// a redirected/401 probe means the session is gone and only a reload helps.
	useEffect(() => {
		if (!user) return
		let es: EventSource | null = null
		let stopped = false
		let failures = 0
		let timer: ReturnType<typeof setTimeout> | null = null

		const close = () => {
			es?.close()
			es = null
		}
		const scheduleReconnect = () => {
			if (stopped || timer) return
			const delay = Math.min(30_000, 1000 * 2 ** Math.min(failures - 1, 5))
			timer = setTimeout(() => {
				timer = null
				connect()
			}, delay)
		}
		/** true = still authenticated (or unsure) — keep retrying */
		const probeAuth = async (): Promise<boolean> => {
			try {
				const res = await fetch('/resources/work', {
					method: 'GET',
					redirect: 'manual',
					headers: { accept: 'application/json' },
				})
				if (
					res.type === 'opaqueredirect' ||
					res.status === 401 ||
					res.status === 403 ||
					(res.status >= 300 && res.status < 400)
				) {
					stopped = true
					close()
					setConnection('auth_lost')
					return false
				}
			} catch {
				// network trouble, not an auth verdict — keep retrying
			}
			return true
		}
		const connect = () => {
			if (stopped) return
			close()
			es = new EventSource('/resources/work-stream')
			es.onopen = () => {
				failures = 0
				setConnection('connected')
			}
			es.onerror = () => {
				if (stopped) return
				failures += 1
				setConnection('reconnecting')
				close()
				if (failures >= 2) {
					void probeAuth().then((stillAuthed) => {
						if (stillAuthed) scheduleReconnect()
					})
				} else {
					scheduleReconnect()
				}
			}
			es.addEventListener('snapshot', (e) => {
				if (stopped) return
				// never dropped — see newest()
				setMine(JSON.parse((e as MessageEvent).data) as WorkMine)
			})
		}
		connect()
		return () => {
			stopped = true
			if (timer) clearTimeout(timer)
			close()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- reconnect only when the signed-in user changes
	}, [user?.id])

	// --- actions ---------------------------------------------------------------------------
	const act = useCallback(async (action: WorkAction): Promise<ActResult> => {
		const fd = new FormData()
		for (const [k, v] of Object.entries(action)) {
			if (v === undefined || v === null) continue
			fd.set(k, typeof v === 'object' ? JSON.stringify(v) : String(v))
		}
		setBusy(true)
		try {
			const res = await fetch('/resources/work', { method: 'POST', body: fd })
			const json = (await res.json()) as any
			if (!res.ok || json?.error) {
				const error = String(json?.error ?? `request failed (${res.status})`)
				setLastError(error)
				return { ok: false, error }
			}
			setLastError(null)
			if (json && typeof json === 'object' && 'open' in json) {
				setMine((held) => newest(held, json as WorkMine))
				return { ok: true, mine: json as WorkMine, createdId: json.created_id }
			}
			return { ok: true, mine: null }
		} catch {
			setLastError('Something went wrong updating your work items.')
			return {
				ok: false,
				error: 'Something went wrong updating your work items.',
			}
		} finally {
			setBusy(false)
		}
	}, [])

	// --- activity trail ---------------------------------------------------------------------
	const activeId = mine?.active?.id ?? null
	useEffect(() => {
		setActiveItemForActivity(activeId)
	}, [activeId])
	useEffect(() => {
		if (!user) return
		return installActivityRecorder()
		// eslint-disable-next-line react-hooks/exhaustive-deps -- reinstall only when the signed-in user changes
	}, [user?.id])
	const lastNav = useRef<string | null>(null)
	useEffect(() => {
		if (!user || !mine) return
		if (lastNav.current === here) return
		lastNav.current = here
		recordActivity({ kind: 'app.navigate', route: here, item_id: activeId })
	}, [here, user, mine, activeId])

	// The history flag counts ONLY for a tab that was OPENED with it — read once,
	// at load, never again. A tab that merely navigates to a flagged URL later
	// (goHome with a poisoned last-Home-page key, Back, a pasted link) is an
	// ordinary tab and must keep its popups. See the auto-open effect below.
	const historyWindow = useRef(hasHistoryFlag(location.search))

	// --- remember the last Home page ---------------------------------------------------------
	useEffect(() => {
		if (!mine || !isHomePage(location.pathname)) return
		// localStorage is shared by every tab. A history side tab must never
		// record its flagged URL here: the MAIN tab's goHome() would land on it.
		if (hasHistoryFlag(location.search)) return
		// Nor the param-less records pages it moves on to: they are the PREVIOUS
		// caller's customer/job, and the main tab's automatic Home after wrap-up
		// would land there. Once it leaves records it is ordinary and writes again.
		if (historyWindow.current && RECORDS_PAGE.test(location.pathname)) return
		try {
			localStorage.setItem(HOME_KEY, here)
		} catch {}
	}, [here, location.pathname, location.search, mine])

	// --- call tabs ---------------------------------------------------------------------------
	const openCall = useCallback(
		(item: WorkItemView) => {
			// optimistic: highlight + navigate immediately; the activate round trip reconciles
			setMine((prev) =>
				prev
					? {
							...prev,
							active: item,
							tabs: prev.tabs.includes(item.id)
								? prev.tabs
								: [...prev.tabs, item.id],
						}
					: prev,
			)
			void navigate(`/call/${item.id}`)
			void act({ intent: 'activate', id: item.id, route: `/call/${item.id}` })
		},
		[act, navigate],
	)

	const goHome = useCallback(() => {
		setMine((prev) => (prev ? { ...prev, active: null } : prev))
		let target = '/dashboard'
		try {
			// strip the flag from whatever is stored, so a key poisoned before
			// this shipped heals the first time it is used
			target = withoutHistoryFlag(
				localStorage.getItem(HOME_KEY) || '/dashboard',
			)
		} catch {}
		void act({ intent: 'deactivate' })
		if (target !== here) void navigate(target)
	}, [act, navigate, here])

	// --- the ACTIVE call opens its window -----------------------------------------------------
	// The server protects live conversations, but a CSR may still be writing wrap-up notes
	// after hanging up. Keep that call workspace in front until they choose or answer the new call.
	const autoOpened = useRef<Set<number>>(new Set())
	// A history link opens a separate tab specifically to leave intake in place.
	// Reading records there is never yanked away — but ONLY while it is still
	// reading them: the URL carries ?callHistory=1, or it moved on to another
	// records page. A suppressed call is deliberately NOT marked as opened, so
	// the moment the CSR takes this tab elsewhere it is an ordinary tab again
	// and a ringing/answered call opens as it would anywhere else.
	// Records = the history viewers AND the detail pages they link to (invoice,
	// estimate, a past call, call search): the CSR is normally ON the call whose
	// history they are reading, so without those a click from a job to its
	// invoice would hand the side tab to that call's workspace. The call log
	// (/calls) is a working page, not a record.
	// (`historyWindow` is declared above, beside the Home-key effect that shares it.)
	// It holds back ONE call only: the one that was active when the tab's
	// first snapshot arrived, i.e. the call whose history is being read
	// (undefined = no snapshot yet, null = there was none). The banner's
	// "Customer Search (new tab)" is a working tool a CSR may stay in for the
	// rest of the day; a brand-new call there is new work and must pop.
	const historyFor = useRef<number | null | undefined>(undefined)
	useEffect(() => {
		// the basic caller-ID shell shows the call where it is; nothing is opened
		// over it (and, like a history window, the call is NOT marked as opened:
		// back in the full app it pops as it would anywhere else)
		if (BASIC_SHELL.test(location.pathname)) return
		// Live Calls shows a pop-up instead; not marked as opened either, so
		// leaving Live Calls during the call opens it as anywhere else
		if (LIVE_CALLS.test(location.pathname)) return
		const a = mine?.active
		if (
			historyWindow.current &&
			(hasHistoryFlag(location.search) || RECORDS_PAGE.test(location.pathname))
		) {
			if (historyFor.current === undefined && a !== undefined)
				historyFor.current = a?.id ?? null
			if (!a || a.id === historyFor.current) return
		} else {
			historyWindow.current = false
		}
		if (!a || a.type !== 'call') return
		// A confirmed outbound snapshot must not select a workspace, including on
		// reconnect. If it was already deliberately opened, leave its editor alone.
		if (a.call?.direction === 'outbound') return
		// a Boss Call (a live line) opens its live page, not the call screen
		const liveLine = a.call?.live_line
		if (liveLine) {
			if (autoOpened.current.has(a.id)) return
			autoOpened.current.add(a.id)
			if (!location.pathname.startsWith(`/live/${liveLine}`))
				void navigate(`/live/${liveLine}`)
			return
		}
		// Viewing call details is an explicit choice, including for our active call.
		if (/^\/calls\/\d+(?:\/|$)/.test(location.pathname)) return
		if (
			/^\/calls?\/\d+(?:\/|$)/.test(location.pathname) &&
			location.pathname !== `/call/${a.id}` &&
			!a.call?.claimed_by_me
		)
			return
		if (autoOpened.current.has(a.id)) return
		autoOpened.current.add(a.id)
		if (location.pathname !== `/call/${a.id}`) {
			void navigate(`/call/${a.id}`)
		}
	}, [mine?.active, location.pathname, location.search, navigate])

	const value = useMemo<WorkContextValue>(
		() => ({
			mine: user ? mine : EMPTY,
			connected: connection === 'connected',
			connection,
			busy,
			lastError,
			act,
			openCall,
			goHome,
			here,
		}),
		[user, mine, connection, busy, lastError, act, openCall, goHome, here],
	)
	return <WorkContext.Provider value={value}>{children}</WorkContext.Provider>
}

export function useWork(): WorkContextValue {
	const ctx = useContext(WorkContext)
	if (!ctx) throw new Error('useWork must be used inside <WorkProvider>')
	return ctx
}
